import { useState } from "react"
import { toast } from "@/clientWeb/components/ui/toast-utils"
import { CreditCard, Download, Calendar, DollarSign } from "lucide-react"
import { Button } from "@/clientWeb/components/ui/button"
import { Badge } from "@/clientWeb/components/ui/badge"
import { useTranslation } from "@/clientWeb/i18n/LanguageContext"


import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/clientWeb/components/ui/select"
import {
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
} from "@/clientWeb/components/ui/table"

interface SummaryCard {
  id: string
  title: string
  value: string | number
  icon: React.ReactNode
  iconBg: string
  iconColor: string
}

interface Payment {
  id: string
  date: string
  amount: number
  status: "completed" | "pending" | "failed"
  description: string
  invoice: string
}

// The same words the status filter uses for each status.
const STATUS_LABEL_KEYS: Record<Payment["status"], string> = {
  completed: "paymentHistory.paid",
  pending: "paymentHistory.pending",
  failed: "paymentHistory.failed",
}

export default function PaymentHistory() {
  const t = useTranslation()
  const [filterStatus, setFilterStatus] = useState<"all" | "completed" | "pending" | "failed">("all")

  // There is no billing backend yet: nothing records a payment (the Licenses
  // checkout is a mock that never charges), so there is nothing to list. This
  // used to show six invented "Professional Plan" charges to every user as
  // their own. Load from the payments API here once one exists.
  const payments: Payment[] = []

  const filteredPayments = filterStatus === "all"
    ? payments
    : payments.filter(p => p.status === filterStatus)

  const totalAmount = filteredPayments.reduce((sum, p) => sum + p.amount, 0)

  const summaryCards: SummaryCard[] = [
    {
      id: "total-spent",
      title: t("paymentHistory.totalSpent"),
      value: `$${totalAmount.toFixed(2)}`,
      icon: <DollarSign className="w-6 h-6" />,
      iconBg: "bg-primary/10",
      iconColor: "text-primary",
    },
    {
      id: "completed",
      title: t("paymentHistory.paid"),
      value: payments.filter(p => p.status === "completed").length,
      icon: <CreditCard className="w-6 h-6" />,
      iconBg: "bg-green-100 dark:bg-green-900/30",
      iconColor: "text-green-600 dark:text-green-400",
    },
    {
      id: "transactions",
      title: t("paymentHistory.totalTransactions"),
      value: payments.length,
      icon: <Calendar className="w-6 h-6" />,
      iconBg: "bg-blue-100 dark:bg-blue-900/30",
      iconColor: "text-blue-600 dark:text-blue-400",
    },
  ]

  const getStatusColor = (status: string) => {
    switch (status) {
      case "completed":
        return "bg-emerald-500/10 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-500/30"
      case "pending":
        return "bg-amber-500/10 text-amber-700 dark:bg-amber-500/20 dark:text-amber-400 border border-amber-200 dark:border-amber-500/30"
      case "failed":
        return "bg-rose-500/10 text-rose-700 dark:bg-rose-500/20 dark:text-rose-400 border border-rose-200 dark:border-rose-500/30"
      default:
        return "bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-400 border border-stroke-100 dark:border-border dark:border-slate-600"
    }
  }

  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
      {/* Page Header with Filter Dropdown */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-900 dark:text-foreground/80 dark:text-white">
            {t("paymentHistory.title")}
          </h2>
          <p className="text-muted-foreground text-sm  mt-1">
            {t("paymentHistory.subtitle")}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={filterStatus} onValueChange={(value) => { setFilterStatus(value as any); toast.info({ title: t("common.filterApplied"), description: t("paymentHistory.showingPayments") }) }}>
            <SelectTrigger className="max-w-[180px]" variant="outlined" size="md">
              <SelectValue placeholder={t("common.all")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("common.all")}</SelectItem>
              <SelectItem value="completed">{t("paymentHistory.paid")}</SelectItem>
              <SelectItem value="pending">{t("paymentHistory.pending")}</SelectItem>
              <SelectItem value="failed">{t("paymentHistory.failed")}</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-3">
        {summaryCards.map((card) => (
          <div
            key={card.id}
            className="relative bg-card dark:bg-card border border-stroke-100 dark:border-border dark:border-border rounded-2xl p-4 shadow-xs hover:shadow-md transition-all duration-200 overflow-hidden"
          >
            {/* Decorative Background Wave */}
            <div className={`absolute inset-0 ${card.iconBg}`}>
              <svg
                className="absolute bottom-0 right-0 w-full h-full"
                viewBox="0 0 200 120"
                fill="none"
                preserveAspectRatio="none"
              >
                <path
                  d="M200 120V80C180 70 160 75 140 80C120 85 100 90 80 85C60 80 40 70 20 75C10 77.5 5 79 0 80V120H200Z"
                  fill="currentColor"
                  className="text-white/30 dark:text-white/10"
                />
              </svg>
            </div>

            {/* Content aligned like ManageRooms stats */}
            <div className="relative z-10 flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-card/50 dark:bg-card/10 flex items-center justify-center shrink-0">
                <div className={card.iconColor}>{card.icon}</div>
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-2xl font-bold text-slate-900 dark:text-foreground/80 dark:text-white leading-none">
                  {card.value}
                </div>
                <div className="text-sm font-medium text-slate-600 dark:text-slate-400 mt-1 truncate">
                  {card.title}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Payment History Table */}
      <div className="bg-card dark:bg-card border border-stroke-100 dark:border-border rounded-xl overflow-hidden">
        {payments.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-30">
            <div className="w-32 h-32 rounded-full flex items-center justify-center mb-4">
              <img src="/icons/noMeetingsNew.svg" alt="" />
            </div>
            <h3 className="text-md font-semibold text-slate-900 dark:text-foreground/80 dark:text-white mb-2">
              {t("paymentHistory.noPayments")}
            </h3>
          </div>
        ) : (
          <div className="max-h-[500px] overflow-y-auto">
            <Table className="w-full">
              <TableHeader>
                <TableRow className="bg-slate-50 dark:bg-slate-700/50">
                  <TableHead className="text-slate-700 dark:text-slate-300">{t("paymentHistory.date")}</TableHead>
                  <TableHead className="text-slate-700 dark:text-slate-300">{t("paymentHistory.description")}</TableHead>
                  <TableHead className="text-slate-700 dark:text-slate-300">{t("paymentHistory.invoice")}</TableHead>
                  <TableHead className="text-slate-700 dark:text-slate-300">{t("paymentHistory.amount")}</TableHead>
                  <TableHead className="text-slate-700 dark:text-slate-300">{t("paymentHistory.status")}</TableHead>
                  <TableHead className="text-slate-700 dark:text-slate-300">{t("myFiles.actions")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredPayments.map((payment) => (
                  <TableRow
                    key={payment.id}
                    className="border-b border-stroke-100 dark:border-border dark:border-border hover:bg-slate-50 dark:hover:bg-slate-700/30 transition-colors"
                  >
                    <TableCell className="text-slate-600 dark:text-slate-400">
                      {payment.date}
                    </TableCell>
                    <TableCell className="font-medium text-slate-900 dark:text-foreground/80 dark:text-white">
                      {payment.description}
                    </TableCell>
                    <TableCell className="text-slate-600 dark:text-slate-400">
                      {payment.invoice}
                    </TableCell>
                    <TableCell className="font-semibold text-slate-900 dark:text-foreground/80 dark:text-white">
                      ${payment.amount.toFixed(2)}
                    </TableCell>
                    <TableCell>
                      <Badge className={getStatusColor(payment.status)}>
                        {t(STATUS_LABEL_KEYS[payment.status])}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-primary hover:text-primary/80"
                        onClick={() => toast.success({ title: t("paymentHistory.invoiceDownloaded"), description: t("paymentHistory.invoiceSaved") })}
                      >
                        <Download className="w-4 h-4 mr-2" />
                        {t("paymentHistory.invoice")}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        {payments.length > 0 && filteredPayments.length === 0 && (
          <div className="text-center py-12">
            <CreditCard className="w-16 h-16 text-slate-300 dark:text-slate-600 mx-auto mb-4" />
            <p className="text-slate-600 dark:text-slate-400">
              {t("paymentHistory.noPaymentsFound")}
            </p>
          </div>
        )}
      </div>
    </div>
  )
}
